import { z } from 'zod';
import { IsoDate, IsoDateTime, QueryBoolean, Uuid } from './common.js';
/** Période d'analyse (dates civiles du tenant, bornes incluses). */
export const PeriodQuery = z.object({
    from: IsoDate,
    to: IsoDate,
    propertyId: Uuid.optional(),
    /** Période de comparaison automatique (même durée juste avant). */
    compare: QueryBoolean.default(false),
});
/** Une période inversée est une erreur de saisie, pas une période vide (DEF-008). */
export function withOrderedPeriod(schema) {
    return schema.refine((value) => value.from <= value.to, {
        path: ['to'],
        message: 'La date de fin doit être postérieure ou égale à la date de début',
    });
}
export const PeriodQueryStrict = withOrderedPeriod(PeriodQuery);
/** Valeur d'un indicateur, avec sa comparaison à la période précédente. */
export const Metric = z.object({
    value: z.number(),
    previous: z.number().nullable(),
    deltaPercent: z.number().nullable(),
});
/** KPI du tableau de bord — Performance et Finance (FR-DSH-02/03). */
export const DashboardKpis = z.object({
    period: z.object({ from: IsoDate, to: IsoDate, days: z.number().int() }),
    currencyCode: z.string().length(3),
    performance: z.object({
        revenue: Metric,
        occupancyRate: Metric,
        averageDailyRate: Metric,
        revPar: Metric,
        averageStayNights: Metric,
        reservationsCount: Metric,
        cancelledCount: Metric,
        noShowCount: Metric,
    }),
    /**
     * Volet Finance (FR-DSH-03) — `null` pour un utilisateur qui n'a pas
     * `report.financial`. La réception voit son tableau de bord opérationnel
     * (FR-DSH-02) sans qu'on lui serve des zéros à la place des montants : un
     * chiffre absent se lit, un faux zéro se croit.
     */
    finance: z
        .object({
        invoiced: Metric,
        collected: Metric,
        outstanding: Metric,
        overdue: Metric,
        deposits: Metric,
        refunds: Metric,
        /**
         * Dépenses enregistrées (non annulées) de la période (sprint 2.9, D-052).
         * Facultatif pour rester rétro-compatible avec les clients du sprint 2.7.
         */
        expenses: Metric.optional(),
    })
        .nullable(),
    revenueByResource: z.array(z.object({
        resourceId: Uuid,
        resourceName: z.string(),
        typeCode: z.string(),
        revenue: z.number(),
        nights: z.number(),
        occupancyRate: z.number(),
    })),
    revenueBySource: z.array(z.object({ source: z.string(), revenue: z.number(), reservationsCount: z.number().int() })),
    /** Série quotidienne pour le graphique. */
    daily: z.array(z.object({
        date: IsoDate,
        revenue: z.number(),
        /** `null` sans `report.financial` (même règle que le volet Finance). */
        collected: z.number().nullable(),
        occupiedUnits: z.number().int(),
        arrivals: z.number().int(),
        departures: z.number().int(),
    })),
});
/** Recherche globale (FR-DSH-04). */
export const SearchEntityType = z.enum([
    'RESERVATION',
    'CUSTOMER',
    'COMPANY',
    'INVOICE',
    'PROFORMA',
    'PAYMENT',
    'RESOURCE',
    'EXPENSE',
]);
export const SearchQuery = z.object({
    q: z.string().min(1).max(100),
    /** Types séparés par une virgule, ex. `RESERVATION,INVOICE`. */
    types: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
});
export const SearchHit = z.object({
    type: SearchEntityType,
    id: Uuid,
    title: z.string(),
    subtitle: z.string().nullable(),
    reference: z.string().nullable(),
    status: z.string().nullable(),
    /** Route frontend cible (ex. /reservations/<id>). */
    href: z.string(),
    score: z.number(),
});
export const SearchResponse = z.object({
    query: z.string(),
    hits: z.array(SearchHit),
    tookMs: z.number().int(),
});
/** Rapports (FR-DSH-06) — chaque rapport a un endpoint JSON + `?format=csv`. */
export const ReportKind = z.enum([
    'REVENUE',
    'OCCUPANCY',
    'RESERVATIONS',
    'CUSTOMERS',
    'COMPANIES',
    'PAYMENTS',
    'RECEIVABLES',
    'DISCOUNTS',
    'TAXES',
    'RESOURCE_PERFORMANCE',
    'CASH',
    'EXPENSES',
]);
export const ReportQuery = PeriodQuery.extend({
    groupBy: z
        .enum([
        'DAY',
        'WEEK',
        'MONTH',
        'RESOURCE',
        'RESOURCE_TYPE',
        'SOURCE',
        'CUSTOMER',
        'COMPANY',
        'METHOD',
        'CATEGORY',
    ])
        .optional(),
    status: z.string().optional(),
    /**
     * Périmètre des réservations (rapport RESERVATIONS) : `ENGAGED` = confirmées,
     * en séjour ou terminées (défaut, DEF-013 : les annulations ne se cumulent
     * plus dans les totaux) ; `ALL` = tous statuts ; `CANCELLED` = annulées et
     * no-shows seulement.
     */
    scope: z.enum(['ENGAGED', 'ALL', 'CANCELLED']).default('ENGAGED'),
    format: z.enum(['json', 'csv']).default('json'),
});
export const ReportQueryStrict = withOrderedPeriod(ReportQuery);
export const ReportGroupBy = ReportQuery.shape.groupBy.unwrap();
export const ReportColumn = z.object({
    key: z.string(),
    label: z.string(),
    kind: z.enum(['text', 'number', 'money', 'percent', 'date', 'datetime', 'status']),
});
export const ReportResult = z.object({
    kind: ReportKind,
    period: z.object({ from: IsoDate, to: IsoDate }),
    generatedAt: IsoDateTime,
    columns: z.array(ReportColumn),
    rows: z.array(z.record(z.unknown())),
    totals: z.record(z.number()).nullable(),
    rowCount: z.number().int(),
});
/** Synthèse des encaissements pour les KPI de la page Paiements. */
export const PaymentsSummary = z.object({
    currencyCode: z.string().length(3),
    today: z.object({ collected: z.number(), count: z.number().int() }),
    month: z.object({ collected: z.number(), count: z.number().int(), refunds: z.number() }),
    unallocated: z.object({ amount: z.number(), count: z.number().int() }),
    byMethod: z.array(z.object({
        method: z.string(),
        provider: z.string().nullable(),
        amount: z.number(),
        count: z.number().int(),
    })),
});
/** Préférences de notification par utilisateur (canal interne + e-mail). */
export const NotificationTopic = z.enum([
    'RESERVATION_TO_CONFIRM',
    'OPTION_EXPIRING',
    'ARRIVAL_TODAY',
    'DEPARTURE_TODAY',
    'DOCUMENT_MISSING',
    'PAYMENT_OVERDUE',
    'INVOICE_UNPAID',
    'RESERVATION_CANCELLED',
    'MAINTENANCE',
    'CASH_VARIANCE',
    'DAILY_DIGEST',
]);
export const NotificationPreference = z.object({
    topic: NotificationTopic,
    internal: z.boolean(),
    email: z.boolean(),
});
export const NotificationPreferences = z.object({
    preferences: z.array(NotificationPreference),
    digestHour: z.number().int().min(0).max(23),
});
export const UpdateNotificationPreferencesRequest = NotificationPreferences.partial();
//# sourceMappingURL=analytics.js.map